import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { PlanId, Plan, PlanFeature } from '../utils/plans.config';
import { PLANS_CONFIG, getPlanById, getPlanFeatures, isFeatureIncluded, getPlanLimits } from '../utils/plans.config';

interface UserPlanData {
  planId: PlanId;
  shopDomain: string;
  userEmail: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

interface UsePlanOptions {
  // For static plan access (no database)
  planId?: PlanId;
  
  // For dynamic user plan fetching
  userEmail?: string;
  shopDomain?: string;
  
  // Behavior control
  autoFetch?: boolean;
  fallbackToBasic?: boolean;
}

interface PlanState {
  // Static plan data
  currentPlan: Plan | null;
  availablePlans: Plan[];
  
  // User-specific plan data
  userPlan: UserPlanData | null;
  
  // State management
  isLoading: boolean;
  error: string | null;
}

interface PlanActions {
  // Plan management
  setPlan: (planId: PlanId) => void;
  upgradePlan: (planId: PlanId) => Promise<void>;
  
  // User plan operations
  fetchUserPlan: () => Promise<void>;
  refreshUserPlan: () => Promise<void>;
  
  // Utility functions
  checkFeatureAccess: (featureId: string) => boolean;
  getPlanLimits: () => { activeGames: number; monthlyPlays: number };
  isUnlimited: (limitType: 'activeGames' | 'monthlyPlays') => boolean;
  
  // Plan info
  planName: string;
  planPrice: number;
  isEnterprise: boolean;
  isUnlimitedPlan: boolean;
}

export function usePlan({
  planId: staticPlanId,
  userEmail,
  shopDomain,
  autoFetch = true,
  fallbackToBasic = true
}: UsePlanOptions = {}): PlanState & PlanActions {
  // State management
  const [currentPlanId, setCurrentPlanId] = useState<PlanId>(staticPlanId || 'basic');
  const [userPlan, setUserPlan] = useState<UserPlanData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Determine if we're in user mode or static mode
  const isUserMode = Boolean(userEmail && shopDomain);
  const effectivePlanId = userPlan?.planId || currentPlanId;

  // Get current plan details
  const currentPlan = useMemo(() => {
    return getPlanById(effectivePlanId) || null;
  }, [effectivePlanId]);

  // Get all available plans
  const availablePlans = useMemo(() => {
    return PLANS_CONFIG;
  }, []);

  // Set plan (for testing or manual override)
  const setPlan = useCallback((planId: PlanId) => {
    setCurrentPlanId(planId);
  }, []);

  // Upgrade plan (would integrate with billing system)
  const upgradePlan = useCallback(async (planId: PlanId) => {
    try {
      setIsLoading(true);
      setError(null);

      // Here you would typically:
      // 1. Call your billing API to upgrade the plan
      // 2. Update the user's plan in your database
      // 3. Handle any necessary redirects or confirmations

      // For now, we'll just simulate the upgrade
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setCurrentPlanId(planId);
      
      // If in user mode, refresh user data
      if (isUserMode) {
        await fetchUserPlan();
      }
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upgrade plan');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [isUserMode]);

  // Cache for user plan data (per user+shop combination)
  const cacheKeyRef = useRef<string>('');
  const cacheRef = useRef<UserPlanData | null>(null);

  // Fetch user plan from database
  const fetchUserPlan = useCallback(async () => {
    if (!isUserMode) {
      return;
    }

    if (!userEmail || !shopDomain) {
      const errorMsg = `Missing ${!userEmail ? 'userEmail' : 'shopDomain'}`;
      setError(errorMsg);
      return;
    }

    // Check cache first
    const cacheKey = `${userEmail}_${shopDomain}`;
    if (cacheKeyRef.current === cacheKey && cacheRef.current) {
      setUserPlan(cacheRef.current);
      setCurrentPlanId(cacheRef.current.planId);
      return;
    }


    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`/api/user-plan?userEmail=${encodeURIComponent(userEmail)}&shopDomain=${encodeURIComponent(shopDomain)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch user plan: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success && data.data) {
        // Update cache
        cacheKeyRef.current = cacheKey;
        cacheRef.current = data.data;
        
        setUserPlan(data.data);
        // Update current plan ID to match user's plan
        setCurrentPlanId(data.data.planId);
      } else {
        const errorMsg = data.error || 'Failed to fetch user plan data';
        console.error('usePlan: API returned error:', errorMsg);
        throw new Error(errorMsg);
      }
    } catch (err) {
      console.error('usePlan: Error fetching user plan:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch user plan';
      setError(errorMessage);
      
      // Fallback to basic plan if enabled and it's a network error
      if (fallbackToBasic && (errorMessage.includes('Failed to fetch') || errorMessage.includes('Network'))) {
        setUserPlan({
          planId: 'basic',
          shopDomain: shopDomain!,
          userEmail: userEmail!,
          isActive: true,
          createdAt: new Date(),
          updatedAt: new Date(),
        });
        setCurrentPlanId('basic');
      }
    } finally {
      setIsLoading(false);
    }
  }, [userEmail, shopDomain, isUserMode, fallbackToBasic]);

  // Refresh user plan (same as fetch but can be called manually)
  const refreshUserPlan = useCallback(async () => {
    await fetchUserPlan();
  }, [fetchUserPlan]);

  // Auto-fetch when dependencies change (only in user mode)
  useEffect(() => {
    if (autoFetch && isUserMode) {
      fetchUserPlan();
    }
  }, [autoFetch, isUserMode, userEmail, shopDomain]);

  // Check if user has access to a specific feature
  const checkFeatureAccess = useCallback((featureId: string): boolean => {
    return isFeatureIncluded(effectivePlanId, featureId, userPlan?.createdAt);
  }, [effectivePlanId, userPlan?.createdAt]);

  // Get current plan limits
  const getPlanLimitsData = useCallback(() => {
    return getPlanLimits(effectivePlanId);
  }, [effectivePlanId]);

  // Check if a specific limit is unlimited
  const isUnlimited = useCallback((limitType: 'activeGames' | 'monthlyPlays'): boolean => {
    const limits = getPlanLimits(effectivePlanId);
    return limits[limitType] === -1;
  }, [effectivePlanId]);

  // Computed values
  const planName = currentPlan?.name || 'Basic';
  const planPrice = currentPlan?.price || 14.99;
  const isEnterprise = effectivePlanId === 'enterprise';
  const isUnlimitedPlan = isUnlimited('monthlyPlays') || isUnlimited('activeGames');

  return {
    // State
    currentPlan,
    availablePlans,
    userPlan,
    isLoading,
    error,
    
    // Actions
    setPlan,
    upgradePlan,
    fetchUserPlan,
    refreshUserPlan,
    
    // Utility functions
    checkFeatureAccess,
    getPlanLimits: getPlanLimitsData,
    isUnlimited,
    
    // Plan info
    planName,
    planPrice,
    isEnterprise,
    isUnlimitedPlan,
  };
}

// Cache for user plan data to avoid duplicate API calls
const userPlanCache = new Map<string, { data: any; timestamp: number }>();
const USER_PLAN_CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

/**
 * Get user's current plan limits for quota calculation
 * @param userEmail - The user's email
 * @param shopDomain - The shop domain
 * @returns Promise with plan limits or null if not found
 */
export const getUserPlanLimits = async (userEmail?: string, shopDomain?: string): Promise<{ activeGames: number; monthlyPlays: number } | null> => {
  if (!userEmail && !shopDomain) {
    return null;
  }

  // Check cache first
  const cacheKey = `${userEmail}_${shopDomain}`;
  const cachedData = userPlanCache.get(cacheKey);
  
  // Use a stable timestamp to avoid hydration mismatch
  const now = typeof window !== 'undefined' ? Date.now() : 0;

  if (cachedData && (now - cachedData.timestamp) < USER_PLAN_CACHE_DURATION) {
    if (cachedData.data?.planId) {
      const plan = getPlanById(cachedData.data.planId);
      return plan?.limits || null;
    }
    return null;
  }

  try {
    const response = await fetch(`/api/user-plan?userEmail=${encodeURIComponent(userEmail || '')}&shopDomain=${encodeURIComponent(shopDomain || '')}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch user plan: ${response.status}`);
    }

    const data = await response.json();
    
    // Update cache
    if (data.success) {
      userPlanCache.set(cacheKey, {
        data: data.data,
        timestamp: now
      });
    }
    
    if (data.success && data.data?.planId) {
      const plan = getPlanById(data.data.planId);
      return plan?.limits || null;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching user plan limits:', error);
    return null;
  }
};

// Convenience hooks for specific use cases
export function usePlanFeature(planId: PlanId, featureId: string): boolean {
  return isFeatureIncluded(planId, featureId);
}

export function usePlanLimits(planId: PlanId) {
  return useMemo(() => getPlanLimits(planId), [planId]);
}

export function usePlanComparison(planId1: PlanId, planId2: PlanId) {
  return useMemo(() => {
    const plan1 = getPlanById(planId1);
    const plan2 = getPlanById(planId2);
    
    if (!plan1 || !plan2) return null;
    
    return {
      priceDifference: plan2.price - plan1.price,
      featureDifference: plan2.features.length - plan1.features.length,
      plan1,
      plan2,
    };
  }, [planId1, planId2]);
}

// Legacy export for backward compatibility
export { usePlan as useUserPlan };
